// src/hooks/useUserEndpoints.js
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FUNCTION_URL, FUNCTION_KEY } from '../utils/constants';

/**
 * @param {string} email
 * @param {boolean} enabled
 * @param {boolean} skip
 */
export function useUserEndpoints(email, enabled = true, skip = false) {
  const { getAccessTokenSilently } = useAuth0();

  const [endpoints, setEndpoints] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isActive, setIsActive] = useState(null);

  useEffect(() => {
    if (skip) {
      setEndpoints([]);
      setUserInfo(null);
      setIsActive(true);
      setError(null);
      setLoading(false);
      return;
    }

    if (!enabled || !email) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const fetchEndpoints = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();

        const url = new URL(`${FUNCTION_URL}/api/v1/users/lookup`);
        url.searchParams.set('email', email);

        if (FUNCTION_KEY) {
          url.searchParams.set('code', FUNCTION_KEY);
        }

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const data = await response.json();

        if (mounted) {
          setEndpoints(data.endpoints || []);
          setUserInfo(data);
          setIsActive(data.isActive ?? null);
        }
      } catch (err) {
        console.error('Error fetching user endpoints:', err);
        if (mounted) {
          setError(err);
          setEndpoints([]);
          setIsActive(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchEndpoints();

    return () => {
      mounted = false;
    };
  }, [email, enabled, skip, getAccessTokenSilently]);

  return {
    endpoints,
    userInfo,
    loading,
    error,
    isActive,
    isInactive: isActive === false,
    hasEndpoint: (endpointCode) => {
      return Array.isArray(endpoints) && endpoints.includes(endpointCode);
    },
  };
}